import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { ShieldCheck } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { MockBadge } from '../components/MockBadge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/Select';
import { apiFetch } from '../lib/api';
import { formatDate, formatNumber } from '../lib/format';
import type { SourceStatsResponse } from '../types/api';

const DAY_MS = 24 * 60 * 60 * 1000;

const PERIOD_PRESETS = [
  { value: '7', label: 'Last 7 days', days: 7 },
  { value: '30', label: 'Last 30 days', days: 30 },
  { value: '90', label: 'Last 90 days', days: 90 },
  { value: '365', label: 'Last 12 months', days: 365 },
] as const;

/** `label` for a `contacts.source` value shown to staff — the raw values are already short and meaningful except the free-text Mailchimp import strings, which are shown as-is. */
function sourceLabel(source: string): string {
  if (source === 'signup_form') return 'Signup form';
  if (source === 'checkout') return 'Checkout (WooCommerce)';
  if (source === '(unknown)') return 'Unknown';
  return source;
}

/**
 * Settings → Statistics → Signup sources (P7 item 5, `PLAN-2026-09-23.md`
 * "Tilmeldingskilde som statistik"). Per `contacts.source`, over a chosen
 * period: new contacts and unsubscribed (`sourceStats.repo.ts`; the API still
 * returns revenue, the page does not show it). "Create segment" hands the source off to the
 * Segments page (`SegmenterPage.tsx`'s `?prefill=` mechanism) so staff can
 * turn a source straight into a saved, reusable audience.
 */
export function SourceStatsPage() {
  const navigate = useNavigate();
  const [presetDays, setPresetDays] = useState<number>(30);

  const period = useMemo(() => {
    const to = Date.now();
    return { from: to - presetDays * DAY_MS, to };
  }, [presetDays]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['stats', 'sources', period.from, period.to],
    queryFn: () =>
      apiFetch<SourceStatsResponse>(`/stats/sources?from=${period.from}&to=${period.to}`),
  });
  const items = data?.items ?? [];

  function createSegment(source: string) {
    const prefill = {
      name: `Signup: ${sourceLabel(source)}`,
      filter: { labels: [], excludeLabels: [], fields: [], unsubscribed: false, sources: [source] },
    };
    navigate(`/indstillinger/segmenter?prefill=${encodeURIComponent(JSON.stringify(prefill))}`);
  }

  return (
    <>
      <PageHeader
        title="Signup sources"
        badge={<MockBadge />}
        description={
          <>
            New contacts and unsubscribes per signup source, {formatDate(period.from)}–
            {formatDate(period.to)}. Unsubscribes are counted from 26 Oct 2026; older ones have no
            signup source. Turn a source into a saved audience with &quot;Create segment&quot;.
          </>
        }
        actions={
          <div className="flex items-center gap-2">
            <label htmlFor="period-select" className="text-[13px] text-muted">
              Period
            </label>
            <Select value={String(presetDays)} onValueChange={(v) => setPresetDays(Number(v))}>
              <SelectTrigger id="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_PRESETS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {isLoading ? <div className="text-muted">Loading…</div> : null}
      {isError ? <Alert>The signup source stats could not be loaded.</Alert> : null}
      {!isLoading && !isError && items.length === 0 ? (
        <EmptyState
          icon={<ShieldCheck />}
          title="No signups in this period"
          description="No new contacts or unsubscribes in this period. Try a longer period."
        />
      ) : null}

      {items.length > 0 ? (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-xs text-muted uppercase tracking-wide">
                <th className="px-4 py-2.5 font-medium">Source</th>
                <th className="px-4 py-2.5 font-medium text-right">New contacts</th>
                <th className="px-4 py-2.5 font-medium text-right">Unsubscribed</th>
                <th className="px-4 py-2.5 font-medium" />
              </tr>
            </thead>
            <tbody>
              {items.map((stat) => (
                <tr key={stat.source} className="border-t border-border-soft">
                  <td className="px-4 py-3 font-medium">{sourceLabel(stat.source)}</td>
                  <td className="px-4 py-3 text-right font-mono">
                    {formatNumber(stat.newContacts)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {formatNumber(stat.unsubscribed)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {/* "(unknown)" is a display bucket for NULL/empty sources, not a
                        stored value, so a segment on it would never match anyone. */}
                    {stat.source !== '(unknown)' ? (
                      <Button variant="secondary" onClick={() => createSegment(stat.source)}>
                        Create segment
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}
    </>
  );
}
